/* ─────────────────────────────────────────────────────────────────
   CLOUD DEVELOPMENT — MODULE-BASED COURSE CONTENT
   Imported by MicrointernshipTask.jsx

   Note: Cloud tasks are often console/infra work with no code repo,
   so each topic sets `submissionType` to tell the task page what proof
   to collect:
     'github'     → GitHub repo link only (default if unset)
     'screenshot' → screenshot(s) only, no repo needed
     'both'       → repo link AND screenshot(s) required
───────────────────────────────────────────────────────────────── */

export const CLOUD_DEV_MODULES = [
  {
    module: 1,
    title: "Cloud Fundamentals",
    emoji: "☁️",
    topics: [
      {
        id: 1,
        topic: "Cloud Computing Basics & AWS Account Setup",
        description: "Understand core cloud concepts (IaaS, PaaS, SaaS) and set up a free-tier AWS account and IAM user.",
        motivation: "Welcome to the cloud! Setting up your account securely is the first real step every cloud engineer takes. ☁️",
        resource: {
          name: "AWS — What is Cloud Computing?",
          url: "https://aws.amazon.com/what-is-cloud-computing/",
        },
        task: "Create a free-tier AWS account and set up a secure IAM admin user (not root) with MFA enabled.",
        requirements: [
          "Sign up for AWS Free Tier",
          "Enable MFA (multi-factor authentication) on the root account",
          "Create a new IAM user with AdministratorAccess for daily use",
          "Set a billing alert to avoid unexpected charges",
          "Take a screenshot of your IAM user list and billing alert setup",
        ],
        submissionType: "screenshot",
      },
      {
        id: 2,
        topic: "Launching & Connecting to an EC2 Instance",
        description: "Learn to launch a virtual machine (EC2) on AWS and connect to it via SSH.",
        motivation: "You just rented your first server in the cloud and logged into it — that's real infrastructure work! 🖥️",
        resource: {
          name: "AWS Docs — Get Started with Amazon EC2",
          url: "https://docs.aws.amazon.com/AWSEC2/latest/UserGuide/EC2_GetStarted.html",
        },
        task: "Launch a free-tier EC2 instance (Ubuntu) and connect to it via SSH.",
        requirements: [
          "Launch a t2.micro / t3.micro Ubuntu instance",
          "Create and download a key pair for SSH access",
          "Configure a security group allowing SSH (port 22)",
          "Connect to the instance via SSH from your terminal",
          "Run a basic command (e.g. `uname -a`) to confirm the connection",
          "Take a screenshot of the EC2 dashboard AND your SSH terminal session",
        ],
        submissionType: "screenshot",
      },
    ],
  },
  {
    module: 2,
    title: "Storage & Databases",
    emoji: "🗄️",
    topics: [
      {
        id: 3,
        topic: "Amazon S3 — Static Website Hosting",
        description: "Learn to use Amazon S3 for object storage and host a static website directly from a bucket.",
        motivation: "Your website is now served straight from the cloud — no server required. That's the power of S3! 🪣",
        resource: {
          name: "AWS Docs — Hosting a Static Website on S3",
          url: "https://docs.aws.amazon.com/AmazonS3/latest/userguide/WebsiteHosting.html",
        },
        task: "Build a simple static site (HTML/CSS/JS) and host it on an S3 bucket with public website access.",
        requirements: [
          "Push your static site's source code to a GitHub repository",
          "Create an S3 bucket and enable static website hosting",
          "Upload your site files and set correct public read permissions",
          "Confirm the site loads via the S3 website endpoint URL",
          "Take a screenshot of the live site loading from the S3 URL",
        ],
        submissionType: "both",
      },
      {
        id: 4,
        topic: "Cloud Databases — DynamoDB Basics",
        description: "Learn the fundamentals of a managed NoSQL cloud database using Amazon DynamoDB.",
        motivation: "Managed databases unlocked! No servers to patch, no backups to configure manually — the cloud handles it. 🗃️",
        resource: {
          name: "AWS Docs — DynamoDB Getting Started",
          url: "https://docs.aws.amazon.com/amazondynamodb/latest/developerguide/getting-started-step-1.html",
        },
        task: "Create a DynamoDB table and perform basic operations using the AWS Console.",
        requirements: [
          "Create a table (e.g. 'Students') with a primary key",
          "Add at least 5 sample items using the console",
          "Run a query to filter items by a non-key attribute",
          "Update one item and delete another",
          "Take a screenshot of the table with its items visible",
        ],
        submissionType: "screenshot",
      },
    ],
  },
  {
    module: 3,
    title: "Deployment & Containers",
    emoji: "🚀",
    topics: [
      {
        id: 5,
        topic: "Deploying a Web App to the Cloud",
        description: "Learn to deploy a real Node.js/web application to a cloud platform (Render, AWS, or similar) and make it publicly accessible.",
        motivation: "Your app is live on the internet! Deployment is the moment your code becomes a real product. 🌍",
        resource: {
          name: "Render Docs — Deploy a Web Service",
          url: "https://render.com/docs/deploy-node-express-app",
        },
        task: "Deploy a simple Node.js/Express (or any backend) application to a cloud platform of your choice.",
        requirements: [
          "Push your app's source code to a GitHub repository",
          "Deploy it to Render, AWS, or another cloud provider",
          "Configure environment variables securely (not hardcoded)",
          "Confirm the app is publicly reachable via its live URL",
          "Take a screenshot of the deployed app working in a browser",
        ],
        submissionType: "both",
      },
      {
        id: 6,
        topic: "Introduction to Docker & Containerization",
        description: "Learn to containerize an application using Docker so it runs consistently anywhere.",
        motivation: "Containerized! Your app now runs the same everywhere — this is a skill every modern cloud team needs. 🐳",
        resource: {
          name: "Docker Docs — Get Started",
          url: "https://docs.docker.com/get-started/",
        },
        task: "Containerize a simple application using Docker and run it locally.",
        requirements: [
          "Write a Dockerfile for your application",
          "Include the Dockerfile and app source in your GitHub repository",
          "Build the Docker image successfully (`docker build`)",
          "Run the container and confirm the app works (`docker run`)",
          "Take a screenshot of the running container and the app working",
        ],
        submissionType: "both",
      },
    ],
  },
  {
    module: 4,
    title: "Final Project",
    emoji: "🏆",
    topics: [
      {
        id: 7,
        topic: "Deploy a Full Application to the Cloud",
        description: "Apply everything learned throughout the MicroIntern to deploy a complete application with a live, public URL.",
        motivation: "Outstanding! You took an app from code to a live, publicly accessible cloud deployment. That's certificate-worthy. 🎉",
        resource: {
          name: "AWS Docs — Getting Started Resource Center",
          url: "https://aws.amazon.com/getting-started/",
        },
        task: "Deploy a complete full-stack or backend application to the cloud with a working public URL.",
        requirements: [
          "Push the complete application source code to GitHub",
          "Deploy the backend (and frontend, if applicable) to a cloud platform",
          "Use environment variables for all secrets/config",
          "Confirm the live URL is publicly accessible and fully functional",
          "Include a README.md documenting the architecture and deployment steps",
          "Take a screenshot of the final live application running",
        ],
        submissionType: "both",
      },
    ],
  },
];

/* ─────────────────────────────────────────────────────────────────
   Flat array — used by COURSE_TASKS in MicrointernshipTask.jsx
   Preserves backward-compatible indexing for completedTopics[]
───────────────────────────────────────────────────────────────── */
export const CLOUD_DEV_TASKS = CLOUD_DEV_MODULES.flatMap((m) => m.topics);
